import { useCallback, useSyncExternalStore } from "react";
import {
  getResolvedTheme,
  getTheme,
  setTheme as storeSetTheme,
  subscribe,
  toggleTheme,
  type ResolvedTheme,
  type Theme,
} from "@/lib/theme/store";

export type { ResolvedTheme, Theme };

/**
 * React adapter over the framework-free theme store in `@/lib/theme/store`.
 *
 * The store owns persistence, the DOM class, and OS-preference watching;
 * this hook just subscribes React components to it. To read/mutate the
 * theme from non-React code, import the store directly.
 *
 * @example
 *   const { theme, resolvedTheme, setTheme, toggle } = useThemeMode();
 */
export function useThemeMode() {
  const theme = useSyncExternalStore(subscribe, getTheme);
  const resolvedTheme = useSyncExternalStore(subscribe, getResolvedTheme);

  const setTheme = useCallback((next: Theme) => storeSetTheme(next), []);
  const toggle = useCallback(() => toggleTheme(), []);

  return { theme, resolvedTheme, setTheme, toggle };
}

export default useThemeMode;
